import type { DateInputProps } from "@/shared/types";
import { toTitleCaseUi } from "@/shared/utils/titleCase";
import clsx from "clsx";
import { format, isAfter, isBefore, isSameDay, parseISO } from "date-fns";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";
import { MONTHS, WEEK_DAYS } from "../config/dateInput";
import AgePill from "./AgePill";
import { WrapperInput } from "./WrapperInput";

function DateInput<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  description,
  placeholder = "DD/MM/YYYY",
  disabled,
  required,
  className,
  min,
  max,
  showAge = false,
  yearOrder = "asc",
}: DateInputProps<TFieldValues, TName>) {
  const placeholderText = toTitleCaseUi(placeholder);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const wrapperRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);

        const selectedDate = useMemo(() => {
          if (!field.value) return null;
          const parsed = parseISO(field.value);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        }, [field.value]);

        const today = useMemo(() => new Date(), []);
        const minDate = useMemo(() => (min ? parseISO(min) : undefined), [min]);
        const maxDate = useMemo(() => (max ? parseISO(max) : undefined), [max]);

        // Computed directly from field.value — always in sync
        const age = useMemo(() => {
          if (!selectedDate) return "";
          const birthDate = selectedDate;
          let a = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            a--;
          }
          return a;
        }, [selectedDate, today]);

        // Only true when showAge prop is set AND we actually have an age value
        const hasAge = showAge && selectedDate !== null && age !== "";

        const initialDate = useMemo(() => {
          if (selectedDate) {
            if (minDate && isBefore(selectedDate, minDate)) {
              return minDate;
            }

            if (maxDate && isAfter(selectedDate, maxDate)) {
              return maxDate;
            }

            return selectedDate;
          }

          if (maxDate && isBefore(maxDate, today)) {
            return maxDate;
          }

          return minDate || today;
        }, [selectedDate, minDate, maxDate, today]);

        const [open, setOpen] = useState(false);
        const [showMonths, setShowMonths] = useState(false);
        const [showYears, setShowYears] = useState(false);
        const [month, setMonth] = useState(initialDate.getMonth());
        const [year, setYear] = useState(initialDate.getFullYear());
        const [calendarPosition, setCalendarPosition] = useState({
          vertical: "bottom",
          horizontal: "left",
        });

        // Raw text value shown in the input — tracks what the user is typing
        const [textValue, setTextValue] = useState(
          selectedDate ? format(selectedDate, "dd/MM/yyyy") : ""
        );
        // Inline parse error (only shown when user has typed something invalid)
        const [parseError, setParseError] = useState("");

        // Keep textValue in sync when form value changes externally (e.g. reset)
        useEffect(() => {
          setTextValue(selectedDate ? format(selectedDate, "dd/MM/yyyy") : "");
          setParseError("");
        }, [field.value]);

        const years = useMemo(() => {
          const currentYear = new Date().getFullYear();
          const start = minDate?.getFullYear() || currentYear - 100;
          const end = maxDate?.getFullYear() || currentYear + 100;

          const items = Array.from({ length: end - start + 1 }, (_, i) => start + i);
          return yearOrder === "desc" ? items.reverse() : items;
        }, [minDate, maxDate, yearOrder]);

        const calendarDays = useMemo(() => {
          const firstDay = new Date(year, month, 1).getDay();
          const totalDays = new Date(year, month + 1, 0).getDate();
          const items: (Date | null)[] = [];
          for (let i = 0; i < firstDay; i++) items.push(null);
          for (let i = 1; i <= totalDays; i++) items.push(new Date(year, month, i));
          return items;
        }, [month, year]);

        const isDisabledDate = (date: Date) => {
          if (minDate && isBefore(date, minDate)) return true;
          if (maxDate && isAfter(date, maxDate)) return true;
          return false;
        };

        const getBoundErrorMessage = (date: Date) => {
          if (minDate && isBefore(date, minDate)) {
            return `Date must be after ${format(minDate, "dd/MM/yyyy")}`;
          }

          if (maxDate && isAfter(date, maxDate)) {
            return `Date must be before ${format(maxDate, "dd/MM/yyyy")}`;
          }

          return "";
        };

        const updateCalendarPosition = () => {
          if (!wrapperRef.current) return;
          const rect = wrapperRef.current.getBoundingClientRect();
          const spaceBottom = window.innerHeight - rect.bottom;
          const spaceTop = rect.top;
          const spaceRight = window.innerWidth - rect.left;
          setCalendarPosition({
            vertical: spaceBottom < 340 && spaceTop > 340 ? "top" : "bottom",
            horizontal: spaceRight < 260 ? "right" : "left",
          });
        };

        const handleDateSelect = (date: Date) => {
          if (isDisabledDate(date)) return;
          field.onChange(format(date, "yyyy-MM-dd"));
          setTextValue(format(date, "dd/MM/yyyy"));
          setParseError("");
          setOpen(false);
        };

        const handlePreviousMonth = () => {
          if (
            minDate &&
            (year < minDate.getFullYear() ||
              (year === minDate.getFullYear() && month <= minDate.getMonth()))
          ) {
            return;
          }

          if (month === 0) {
            setMonth(11);
            setYear((prev) => prev - 1);
            return;
          }
          setMonth((prev) => prev - 1);
        };

        const handleNextMonth = () => {
          if (
            maxDate &&
            (year > maxDate.getFullYear() ||
              (year === maxDate.getFullYear() && month >= maxDate.getMonth()))
          ) {
            return;
          }

          if (month === 11) {
            setMonth(0);
            setYear((prev) => prev + 1);
            return;
          }
          setMonth((prev) => prev + 1);
        };

        const syncCalendarToValidDate = () => {
          const calendarDate =
            selectedDate &&
            (!minDate || !isBefore(selectedDate, minDate)) &&
            (!maxDate || !isAfter(selectedDate, maxDate))
              ? selectedDate
              : initialDate;

          setMonth(calendarDate.getMonth());
          setYear(calendarDate.getFullYear());
        };

        /**
         * Auto-format as the user types:
         * - Inserts "/" after DD and after MM automatically
         * - Strips non-numeric characters
         * - Caps length at 10 (DD/MM/YYYY)
         */
        const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;

          // Allow user to backspace freely — only auto-format when growing
          const prev = textValue;
          const isDeleting = raw.length < prev.length;

          if (isDeleting) {
            setTextValue(raw);
            setParseError("");
            return;
          }

          // Strip non-digit characters except "/"
          let digits = raw.replace(/[^\d]/g, "");
          // Cap at 8 digits (DDMMYYYY)
          digits = digits.slice(0, 8);

          // Re-build as DD/MM/YYYY
          let formatted = digits;
          if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
          }
          if (digits.length > 4) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
          }

          setTextValue(formatted);
          setParseError("");

          // When we have a full date, validate + commit immediately
          if (digits.length === 8) {
            commitTextDate(formatted);
          }
        };

        /**
         * Parse and commit DD/MM/YYYY → field.onChange("yyyy-MM-dd")
         * Called on blur OR when 8 digits have been typed.
         */
        const commitTextDate = (value: string) => {
          const trimmed = value.trim();
          if (!trimmed) {
            // User cleared the field
            field.onChange("");
            setParseError("");
            return;
          }

          const parts = trimmed.split("/");
          if (parts.length !== 3) {
            setParseError("Enter date as DD/MM/YYYY");
            return;
          }

          const [dd, mm, yyyy] = parts;
          const d = parseInt(dd, 10);
          const m = parseInt(mm, 10);
          const y = parseInt(yyyy, 10);

          if (
            isNaN(d) ||
            isNaN(m) ||
            isNaN(y) ||
            yyyy.length !== 4 ||
            m < 1 ||
            m > 12 ||
            d < 1 ||
            d > 31
          ) {
            setParseError("Invalid date");
            return;
          }

          const date = new Date(y, m - 1, d);

          // Check the date is real (e.g. 31/02 would roll over)
          if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
            setParseError("Invalid date");
            return;
          }

          if (isDisabledDate(date)) {
            setParseError(getBoundErrorMessage(date));
            return;
          }

          // All good — commit
          setParseError("");
          field.onChange(format(date, "yyyy-MM-dd"));
          // Sync calendar view to the typed date
          setMonth(date.getMonth());
          setYear(date.getFullYear());
        };

        useEffect(() => {
          if (!field.value) {
            setParseError("");
            return;
          }

          const parsed = parseISO(field.value);

          if (Number.isNaN(parsed.getTime())) {
            return;
          }

          const boundError = getBoundErrorMessage(parsed);
          setParseError(boundError);
        }, [field.value, minDate, maxDate]);

        useEffect(() => {
          if (!minDate) {
            return;
          }

          const displayedMonthIsBeforeMinimum =
            year < minDate.getFullYear() ||
            (year === minDate.getFullYear() && month < minDate.getMonth());

          if (displayedMonthIsBeforeMinimum) {
            syncCalendarToValidDate();
          }
        }, [initialDate, minDate, month, selectedDate, year]);

        useEffect(() => {
          if (!maxDate) {
            return;
          }

          const displayedMonthIsAfterMaximum =
            year > maxDate.getFullYear() ||
            (year === maxDate.getFullYear() && month > maxDate.getMonth());

          if (displayedMonthIsAfterMaximum) {
            syncCalendarToValidDate();
          }
        }, [initialDate, maxDate, month, selectedDate, year]);

        useEffect(() => {
          if (textValue.trim()) {
            commitTextDate(textValue);
          }
        }, [minDate, maxDate]);

        const handleBlur = () => {
          commitTextDate(textValue);
        };

        useEffect(() => {
          const handleOutsideClick = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
              setOpen(false);
              setShowMonths(false);
              setShowYears(false);
            }
          };
          document.addEventListener("mousedown", handleOutsideClick);
          return () => document.removeEventListener("mousedown", handleOutsideClick);
        }, []);

        useEffect(() => {
          if (!open) return;
          updateCalendarPosition();
          window.addEventListener("resize", updateCalendarPosition);
          window.addEventListener("scroll", updateCalendarPosition, true);
          return () => {
            window.removeEventListener("resize", updateCalendarPosition);
            window.removeEventListener("scroll", updateCalendarPosition, true);
          };
        }, [open]);

        // Combined error: prefer react-hook-form error, fall back to parse error
        const displayError = fieldState.error?.message || parseError;

        return (
          <WrapperInput
            label={label}
            description={description}
            error={displayError}
            required={required}
            className={className}
          >
            {/*
              Wrapper is always relative + flex.
              gap-2 only when hasAge so no phantom space before date is picked.
            */}
            <div
              ref={wrapperRef}
              className={clsx("relative flex items-center w-full", hasAge ? "gap-2" : "")}
            >
              {/* DATE INPUT — text entry + calendar icon trigger */}
              <div
                className={clsx(
                  "relative flex h-9 items-center rounded-xl border",
                  "border-theme-border-strong bg-theme-surface",
                  "focus-within:border-theme-primary focus-within:ring-2 focus-within:ring-theme-primary/10",
                  hasAge ? "flex-1" : "w-full",
                  disabled && "cursor-not-allowed opacity-50",
                  (fieldState.error || parseError) && "border-red-500"
                )}
              >
                <input
                  ref={inputRef}
                  id={field.name}
                  data-testid={field.name}
                  type="text"
                  inputMode="numeric"
                  disabled={disabled}
                  placeholder={placeholderText}
                  value={textValue}
                  onChange={handleTextChange}
                  onBlur={handleBlur}
                  className={clsx(
                    "h-full w-full bg-transparent pl-4 pr-9 text-[13px] outline-none",
                    "text-theme-text placeholder:text-neutral-400",
                    disabled && "cursor-not-allowed"
                  )}
                />
                {/* Calendar icon — opens/closes picker */}
                <button
                  type="button"
                  disabled={disabled}
                  tabIndex={-1}
                  onClick={() => {
                    updateCalendarPosition();
                    if (!open) {
                      syncCalendarToValidDate();
                      setOpen(true);
                      return;
                    }

                    setOpen(false);
                    // Refocus input after closing so blur doesn't fire oddly
                    if (open) inputRef.current?.focus();
                  }}
                  className="absolute right-3 flex items-center justify-center text-neutral-500 hover:text-neutral-700"
                >
                  <CalendarDays className="h-4 w-4" />
                </button>
              </div>

              {/* AGE BADGE — only mounts after a date is selected */}
              {hasAge && (
                // <div className="flex h-9 shrink-0 items-center justify-center rounded-xl border border-green-200 bg-green-50 px-4">
                //   <span className="whitespace-nowrap text-[13px] font-semibold text-green-700">
                //     Age: {age}
                //   </span>
                // </div>
                <AgePill data={"Age: " + age} className="shrink-0" />
              )}

              {/* CALENDAR DROPDOWN */}
              {open && (
                <div
                  className={clsx(
                    "absolute z-49 w-[260px] bg-white/95 backdrop-blur-md",
                    calendarPosition.vertical === "bottom" && "top-full mt-2",
                    calendarPosition.vertical === "top" && "bottom-full mb-2",
                    calendarPosition.horizontal === "left" && "left-0",
                    calendarPosition.horizontal === "right" && "right-0",
                    "rounded-xl border border-neutral-200 bg-white p-3",
                    "shadow-[0_20px_60px_rgba(0,0,0,0.12)]",
                    "animate-in fade-in zoom-in-95 duration-200"
                  )}
                >
                  {/* HEADER */}
                  <div className="mb-3 flex items-center justify-between gap-2 border-b border-neutral-100 pb-3">
                    <button
                      type="button"
                      disabled={
                        Boolean(minDate) &&
                        (year < minDate!.getFullYear() ||
                          (year === minDate!.getFullYear() && month <= minDate!.getMonth()))
                      }
                      onClick={handlePreviousMonth}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 transition-all duration-200 hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5 text-neutral-700" />
                    </button>

                    <div className="flex items-center gap-2">
                      {/* MONTH */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            setShowMonths((prev) => !prev);
                            setShowYears(false);
                          }}
                          className="flex h-8 items-center gap-1.5 rounded-lg bg-neutral-50 border border-neutral-200 px-3 text-[13px] font-semibold text-neutral-900 transition-all duration-200 hover:bg-neutral-200"
                        >
                          {MONTHS[month]}
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>

                        {showMonths && (
                          <div className="absolute left-0 top-10 z-50 max-h-56 w-36 overflow-y-auto overflow-x-hidden thin-scrollbar rounded-xl border border-neutral-200 bg-white p-1.5 shadow-2xl">
                            {MONTHS.map((monthName, index) => (
                              <button
                                key={monthName}
                                type="button"
                                disabled={
                                  (Boolean(minDate) &&
                                    year === minDate!.getFullYear() &&
                                    index < minDate!.getMonth()) ||
                                  (Boolean(maxDate) &&
                                    year === maxDate!.getFullYear() &&
                                    index > maxDate!.getMonth())
                                }
                                onClick={() => {
                                  setMonth(index);
                                  setShowMonths(false);
                                }}
                                className={clsx(
                                  "flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] font-medium transition-all duration-150 hover:bg-neutral-200",
                                  month === index &&
                                    "bg-theme-primary hover:bg-theme-primary text-white",
                                  minDate &&
                                    year === minDate.getFullYear() &&
                                    index < minDate.getMonth() &&
                                    "cursor-not-allowed opacity-40",
                                  maxDate &&
                                    year === maxDate.getFullYear() &&
                                    index > maxDate.getMonth() &&
                                    "cursor-not-allowed opacity-40"
                                )}
                              >
                                {monthName}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* YEAR */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            setShowYears((prev) => !prev);
                            setShowMonths(false);
                          }}
                          className="flex h-8 items-center gap-1.5 rounded-lg bg-neutral-100 px-3 text-[13px] font-semibold text-neutral-900 transition-all duration-200 hover:bg-neutral-200"
                        >
                          {year}
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>

                        {showYears && (
                          <div className="absolute right-0 top-10 z-50 max-h-56 w-24 overflow-y-auto overflow-x-hidden thin-scrollbar rounded-xl border border-neutral-200 bg-white p-1.5 shadow-2xl">
                            {years.map((yearItem) => (
                              <button
                                key={yearItem}
                                type="button"
                                onClick={() => {
                                  const firstMonth =
                                    minDate && yearItem === minDate.getFullYear()
                                      ? minDate.getMonth()
                                      : 0;
                                  const lastMonth =
                                    maxDate && yearItem === maxDate.getFullYear()
                                      ? maxDate.getMonth()
                                      : 11;
                                  setMonth(Math.min(Math.max(month, firstMonth), lastMonth));
                                  setYear(yearItem);
                                  setShowYears(false);
                                }}
                                className={clsx(
                                  "flex w-full items-center justify-center rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150 hover:bg-neutral-200",
                                  year === yearItem &&
                                    "bg-theme-primary hover:bg-theme-primary text-white"
                                )}
                              >
                                {yearItem}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={
                        Boolean(maxDate) &&
                        (year > maxDate.getFullYear() ||
                          (year === maxDate.getFullYear() && month >= maxDate.getMonth()))
                      }
                      onClick={handleNextMonth}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 transition-all duration-200 hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronRight className="h-3.5 w-3.5 text-neutral-700" />
                    </button>
                  </div>

                  {/* WEEK DAYS */}
                  <div className="mb-1 grid grid-cols-7">
                    {WEEK_DAYS.map((day) => (
                      <div
                        key={day}
                        className="flex h-8 items-center justify-center text-[10px] font-semibold tracking-[0.08em] text-neutral-400"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* DAYS */}
                  <div className="grid grid-cols-7 gap-y-1">
                    {calendarDays.map((date, index) => {
                      if (!date) return <div key={`empty-${index}`} className="h-8 w-8" />;

                      const disabledDate = isDisabledDate(date);
                      const selected = selectedDate && isSameDay(selectedDate, date);
                      const isToday = isSameDay(today, date);

                      return (
                        <button
                          key={date.toISOString()}
                          type="button"
                          disabled={disabledDate}
                          onClick={() => handleDateSelect(date)}
                          className={clsx(
                            "flex h-8 w-8 items-center justify-center rounded-lg text-[13px] font-semibold transition-all duration-150",
                            selected && "bg-theme-primary text-white shadow-none",
                            !selected && !disabledDate && "text-neutral-800 hover:bg-neutral-200",
                            isToday &&
                              !selectedDate &&
                              "border border-neutral-300 text-white hover:bg-theme-primary bg-theme-primary",
                            disabledDate && "cursor-not-allowed opacity-30"
                          )}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </WrapperInput>
        );
      }}
    />
  );
}

export default memo(DateInput);
